<template>
  <section class="pdf-document" :aria-label="title + '预览'">
    <header class="pdf-toolbar">
      <div class="document-heading">
        <span class="document-icon el-icon-document" aria-hidden="true"></span>
        <div>
          <h2>{{title || '处方笺'}}</h2>
          <span class="document-meta">{{pages.length ? '共 ' + pages.length + ' 页' : 'PDF 处方'}}</span>
        </div>
      </div>
      <div class="document-actions">
        <div class="zoom-controls" aria-label="缩放">
          <el-button icon="el-icon-minus" aria-label="缩小" title="缩小" :disabled="!pages.length || zoom <= 50" @click="changeZoom(-25)"></el-button>
          <span class="zoom-value" aria-live="polite">{{zoom}}%</span>
          <el-button icon="el-icon-plus" aria-label="放大" title="放大" :disabled="!pages.length || zoom >= 200" @click="changeZoom(25)"></el-button>
        </div>
        <el-button class="fit-button" :disabled="!pages.length" @click="zoom = 100">适应宽度</el-button>
        <el-button type="primary" icon="el-icon-printer" @click="$emit('print')">打印处方</el-button>
      </div>
    </header>
    <div class="paper-stage">
      <div ref="paperList" class="paper-list">
        <div v-if="loading" class="document-state" role="status">
          <i class="el-icon-loading" aria-hidden="true"></i>
          <p>正在加载处方…</p>
        </div>
        <div v-else-if="error" class="document-state" role="alert">
          <i class="el-icon-document" aria-hidden="true"></i>
          <p>暂时无法预览此处方</p>
          <span>可以重新加载，或打开原文件查看和打印。</span>
          <el-button size="small" plain @click="loadDocument">重新加载</el-button>
        </div>
        <pdf-page-canvas v-for="page in pages" :key="documentVersion + '-' + page.pageNumber" :page="page" :page-count="pages.length" :available-width="availableWidth" :zoom="zoom"></pdf-page-canvas>
      </div>
    </div>
    <footer class="document-footer">
      <span><i class="el-icon-info" aria-hidden="true"></i> 打印使用原 PDF 文件</span>
      <a :href="src" target="_blank" rel="noopener noreferrer">打开原文件 <i class="el-icon-top-right" aria-hidden="true"></i></a>
    </footer>
  </section>
</template>

<script>
import PdfPageCanvas from './PdfPageCanvas.vue'
import { loadPdfEngine, resolvePdfUrl } from '@/js/pdf-preview'

export default {
  components: { PdfPageCanvas },
  props: { src: { type: String, required: true }, title: { type: String, default: '处方笺' } },
  data() {
    return { pages: [], loading: true, error: false, zoom: 100, availableWidth: 800, documentVersion: 0 }
  },
  mounted() {
    this.measureWidth()
    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(this.measureWidth)
      this._resizeObserver.observe(this.$refs.paperList)
    } else {
      window.addEventListener('resize', this.measureWidth)
    }
    this.loadDocument()
  },
  watch: { src: 'loadDocument' },
  beforeDestroy() {
    this._disposed = true
    this.documentVersion++
    if (this._resizeObserver) this._resizeObserver.disconnect()
    window.removeEventListener('resize', this.measureWidth)
    clearTimeout(this._resizeTimer)
    this.releaseDocument()
  },
  methods: {
    measureWidth() {
      clearTimeout(this._resizeTimer)
      this._resizeTimer = setTimeout(() => {
        if (!this._disposed && this.$refs.paperList) this.availableWidth = Math.max(1, this.$refs.paperList.clientWidth)
      }, 100)
    },
    changeZoom(delta) {
      this.zoom = Math.max(50, Math.min(200, this.zoom + delta))
    },
    releaseDocument() {
      if (this._loadingTask) {
        this._loadingTask.destroy().catch(() => {})
        this._loadingTask = null
      }
    },
    loadDocument() {
      const version = ++this.documentVersion
      this.pages = []
      this.loading = true
      this.error = false
      this.zoom = 100
      this.releaseDocument()
      return loadPdfEngine().then(pdfjs => {
        if (this._disposed || version !== this.documentVersion) return
        const task = pdfjs.getDocument({
          url: resolvePdfUrl(this.src, window.location.href),
          cMapUrl: '/static/pdfjs/cmaps/', cMapPacked: true,
          standardFontDataUrl: '/static/pdfjs/standard_fonts/',
          wasmUrl: '/static/pdfjs/wasm/', iccUrl: '/static/pdfjs/iccs/',
          isEvalSupported: false
        })
        this._loadingTask = task
        return task.promise.then(pdf => {
          if (this._disposed || version !== this.documentVersion) return
          return Promise.all(Array.from({ length: pdf.numPages }, (_, index) => pdf.getPage(index + 1)))
        }).then(pages => {
          if (this._disposed || version !== this.documentVersion || !pages) return
          // Keep PDF.js page instances outside Vue's deep observation.
          this.pages = Object.freeze(pages)
          this.loading = false
        })
      }).catch(() => {
        if (this._disposed || version !== this.documentVersion) return
        this.loading = false
        this.error = true
        this.releaseDocument()
      })
    }
  }
}
</script>

<style scoped>
.pdf-document{ width: 100%; border: 1px solid #e4e9ee; border-radius: 10px; background: #fff; overflow: hidden; text-align: left; }
.pdf-toolbar{ display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; padding: 20px 24px; border-bottom: 1px solid #e8edf1; }
.document-heading{ display: flex; align-items: center; gap: 12px; min-width: 0; flex: 1; }
.document-icon{ font-size: 24px; color: #156098; background: #eef5fa; padding: 12px; border-radius: 8px; }
.document-heading h2{ font-size: 18px; line-height: 26px; font-weight: 600; color: #283747; margin: 0; overflow-wrap: anywhere; }
.document-meta{ display: block; margin-top: 3px; font-size: 13px; color: #8a96a3; }
.document-actions{ display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.document-actions .el-button{ margin-left: 0; font-size: 14px; }
.zoom-controls{ display: flex; align-items: center; height: 38px; border: 1px solid #e4e9ee; border-radius: 5px; }
.zoom-controls .el-button{ border: 0; padding: 11px; background: transparent; }
.zoom-value{ min-width: 48px; text-align: center; color: #596777; font-size: 13px; font-variant-numeric: tabular-nums; }
.fit-button{ border-color: #e4e9ee; }
.paper-stage{ background: #f2f4f7; padding: 32px; overflow-x: auto; }
.paper-list{ width: 100%; }
.document-state{ min-height: 300px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #7f8c99; font-size: 14px; text-align: center; }
.document-state > i{ font-size: 30px; color: #a7b5c2; }
.document-state p{ color: #526273; margin: 18px 0 10px; font-size: 16px; }
.document-state .el-button{ margin-top: 20px; }
.document-footer{ display: flex; flex-wrap: wrap; gap: 12px; justify-content: space-between; padding: 16px 24px; color: #8a96a3; font-size: 13px; line-height: 20px; border-top: 1px solid #e4e9ee; }
.document-footer a{ color: #156098; text-decoration: none; }
.document-footer a:hover{ text-decoration: underline; }
@media (max-width: 640px){
  .pdf-toolbar{ padding: 16px; }
  .document-heading{ flex-basis: 100%; }
  .document-actions{ gap: 8px; }
  .document-actions > .el-button{ padding: 11px; }
  .paper-stage{ padding: 16px; }
  .document-footer{ padding: 14px 16px; }
}
</style>



// WEBPACK FOOTER //
// src/components/kaifang/PdfPrescriptionPreview.vue